import {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';
import {TICK_DOMAIN_MY,REACT_APP_ENV} from '../../config/config';
import readCookie from '../../library/readCookie';
import setGlobalState from '../../redux/setGlobalState';
import useMobile from '../../hooks/useMobile';
import {Else, If, Then} from 'react-if';
import BlockPage from './blockPage/BlockPage';
import TickchakLoaderXHR from '../../component/tickchakLoader/TickchakLoaderXHR';
import zIndex from '../../globalVariables/zIndex';
import * as css from './MyIframe.css';
import {NODE_ENV} from './../../../src/config/config';

// Timeout for iframe loading (in milliseconds)
const WHATSAPP_IFRAME_TIMEOUT = 10000; // 10 seconds

export const reloadIframe=()=>{
    setGlobalState('ui.layout.isReloadIframe',true);
};

const getSelectedQueryParameters = (selectedParams) => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    let result = [];

    for(const param of selectedParams) {
        if(queryParams.has(param)) {
            result.push(`${param}=${queryParams.get(param)}`);
        }
    }

    return result.length > 0 ? '&' + result.join('&') : '';
};

function MyIframe({location: propLocation, iframeRef,display,src=null}) {
    const hookLocation = useLocation();
    const location = propLocation || hookLocation;
    const tickchakToken = readCookie(`tickchak_user_${NODE_ENV}`);
    const isReloadIframe=useSelector(s=>s.ui.layout.isReloadIframe);
    const isScheduled=useSelector(s=>s.ui.event.isScheduled);
    const eventEid = useSelector(s => s.event.eid);
    const eventType = useSelector(s => s.event.type);
    const packagePid = useSelector(s => s.package?.pid);
    const [iframeId,setIframeId]=useState(1);
    const [isLoading,setIsLoading]=useState(true);
    const timer=useRef(null);
    const loadTimeoutRef = useRef(null);
    const page = location?.pathname?.split('/').pop() || '';
    const [blockPage, setBlockPage]=useState(true);
    const isMobile = useMobile();
    const reloadXHR=()=>{
        setIsLoading(false);
        clearTimeout(timer.current);
        timer.current=setTimeout(() => {
            setIsLoading(true);
        },300);
    };

    // Ensure proper cleanup of timers in React 18
    useEffect(() => {
        return () => {
            clearTimeout(timer.current);
            clearTimeout(loadTimeoutRef.current);
        };
    }, []);

    // Update useEffect to handle strict mode double invocation
    useEffect(() => {
        reloadXHR();
    }, [location?.pathname]);

    useEffect(()=>{
        if(isReloadIframe){
            reloadXHR();
            setIframeId(prev=>prev+1);
            setGlobalState('ui.layout.isReloadIframe',false);
        }
    },[isReloadIframe]);

    useEffect(()=>{
        if(page!=='realtime')
            setBlockPage(true);
    },[location?.pathname ]);

    // Timeout fallback for WhatsApp iframe
    useEffect(() => {
        const isWhatsAppPage = location?.pathname?.includes('/publish/whatsapp') ||
                              location?.pathname?.includes('/whatsapp');

        if(isWhatsAppPage && isLoading) {
            // Clear any existing timeout
            clearTimeout(loadTimeoutRef.current);

            // Set timeout to hide loader after 10 seconds
            loadTimeoutRef.current = setTimeout(() => {
                console.warn('[MyIframe] WhatsApp iframe timeout - hiding loader after 10s');
                setIsLoading(false);
            }, WHATSAPP_IFRAME_TIMEOUT);
        }

        // Cleanup timeout on unmount or when loading completes
        return () => {
            clearTimeout(loadTimeoutRef.current);
        };
    }, [location?.pathname, isLoading]);

    // Listen for WhatsApp iframe ready message
    useEffect(() => {
        const handleMessage = (event) => {
            // Accept messages from WhatsApp iframe
            if(event.data && event.data.type === 'whatsapp-iframe-ready') {
                console.log('[MyIframe] Received ready message from WhatsApp iframe');
                setIsLoading(false);
                clearTimeout(loadTimeoutRef.current);
            }
        };

        const isWhatsAppPage = location?.pathname?.includes('/publish/whatsapp') ||
                              location?.pathname?.includes('/whatsapp');

        if(isWhatsAppPage) {
            window.addEventListener('message', handleMessage);
        }

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [location?.pathname]);

    let iframeSrc='';
    if(src) {
        iframeSrc = `${TICK_DOMAIN_MY}${src}&tickchakNewApp`;
        if(REACT_APP_ENV === 'local'){
            iframeSrc += `&tickchak_token=${tickchakToken}`;
        }
    }
    else {
        iframeSrc = `${TICK_DOMAIN_MY}${display ? location?.pathname : '/p/0'}?tickchakNewApp`;
        iframeSrc += isScheduled ? '&openScheduled=1' : '';

        iframeSrc = iframeSrc.replace('publish/v2', 'publish_givechak');
        iframeSrc = iframeSrc.replace('publishGivechak', 'publish/v2');

        if(eventType === 'timetable') {
            iframeSrc = iframeSrc.replace('timetable/coupons', 'tickets/coupons');
        }

        // Override URL for WhatsApp tab
        if(location?.pathname?.includes('/publish/whatsapp')) {
            // Event-level WhatsApp - use eid instead of pid
            iframeSrc = `https://services.tickchak.co.il/mailing/whatsapp${eventEid ? `?eid=${eventEid}` : ''}`;
        } else if(location?.pathname?.includes('/p/') && location?.pathname?.includes('/whatsapp')) {
            // Package-level WhatsApp - extract pid from URL path /p/{pid}/whatsapp
            const urlPid = packagePid || location?.pathname?.match(/\/p\/(\d+)\//)?.[1];
            iframeSrc = `https://services.tickchak.co.il/mailing/whatsapp${urlPid ? `?pid=${urlPid}` : ''}`;
        }

        if(REACT_APP_ENV === 'local') {
            iframeSrc += `&tickchak_token=${tickchakToken}`;
        }
        iframeSrc += getSelectedQueryParameters(['oid', 'lead', 'rpid', 'area', 'id', 'tid']);
    }

    const isWhatsAppPage = location?.pathname?.includes('/publish/whatsapp');
    const whatsappMobileClass = isMobile && isWhatsAppPage ? 'whatsapp-mobile' : '';

    return (
        <>
            {display&&<If condition={isMobile && blockPage && page === 'design'}>
                <Then>
                    <BlockPage setBlockPage={setBlockPage}/>
                </Then>
                <Else>
                    <TickchakLoaderXHR xhr={isLoading ? 'LOADING' : null}
                        cssStyle={`position: absolute; padding: 40px; z-index:${zIndex.baseRaised};`}/>
                </Else>
            </If>}
            <css.iframe
                className={`${!display && 'hide'} ${whatsappMobileClass}`}
                id={'iframe'}
                key={`mainIframe${iframeId}`}
                ref={iframeRef}
                src={iframeSrc}
                onLoad={()=>setIsLoading(false)}
            />
        </>
    );

}
export default MyIframe;
